import {
  LoginCredentials,
  RegisterAccountCredentials,
  MfaVerificationData,
} from '@typeDefs/domain/auth';
import { authMapper } from '@services/mappers/authMapper';
import type {
  LoginResponse as ApiLoginResponse,
  LoginUserCommand,
  CompleteMfaLoginCommand,
} from '@generated/api';
import apiClient from './client';

// ── Verify-first contract types (hand-authored — not in generated API yet) ────
// These match the Wave 2 backend endpoints for the verify-first registration
// flow (client-api#221, #222, #243). They are hand-authored per the project
// convention (web-client does not use swagger-gen for its auth types).

export interface SendVerificationResponse {
  success: boolean;
  challengeId?: string | null;
  expiresAt?: string | null;
  message?: string | null;
}

/** Typed reason field returned by the verify-email endpoint on failure. */
export type VerifyEmailFailureReason =
  | 'NotFound'
  | 'Expired'
  | 'InvalidCode'
  | 'TooManyAttempts'
  | 'NotVerified'
  | 'VerificationExpired'
  | 'AlreadyUsed'
  | string;

export interface VerifyEmailResponse {
  success: boolean;
  reason?: VerifyEmailFailureReason | null;
  remainingAttempts?: number | null;
  lockedUntil?: string | null;
}

export interface ResendVerificationResponse {
  success: boolean;
  challengeId?: string | null;
  // The resend endpoint returns a fresh server-driven expiry window (#200).
  // The UI re-reads this rather than deriving a local now+TTL fallback.
  expiresAt?: string | null;
  resendAllowedAt?: string | null;
  message?: string | null;
}

// ── Password-reset contract types (hand-authored — not in generated API yet) ──
// Match the client-api confirm endpoint (POST /api/auth/reset-password). Built
// hand-wrapped (mirroring sendVerificationEmail) so the page + tests compile
// against MSW ahead of typegen; swap to the generated method once it exists.
// (35-password-reset-flow, web-client #212)

export interface ResetPasswordRequest {
  email: string;
  token: string;
  newPassword: string;
}

/**
 * Canonical PascalCase failure reasons returned by the confirm endpoint on a
 * 400. Branch the UI copy on these MEMBER strings — never on free text.
 */
export type PasswordResetFailureReason =
  'InvalidToken' | 'ExpiredToken' | 'TokenAlreadyUsed' | 'UserNotFound' | string;

export interface ResetPasswordResponse {
  success: boolean;
  reason?: PasswordResetFailureReason | null;
  message?: string | null;
}

// ── Forgot-password (request side) contract types ─────────────────────────────
// Match the client-api request endpoint (POST /api/auth/forgot-password). Built
// hand-wrapped (mirroring sendVerificationEmail / resetPassword) so the page +
// tests compile against MSW ahead of typegen.
// Anti-enumeration: the endpoint ALWAYS returns a neutral 200 ("if an account
// exists for that email, a reset link has been sent") regardless of whether the
// account exists. The UI must never branch on existence.
// (35-password-reset-flow, web-client #213)

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  message?: string | null;
}

// Helper function to get IP address (client-side approximation)
const getClientIpAddress = async (): Promise<string> => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.warn('Failed to get IP address:', error);
    return 'unknown';
  }
};

// Helper function to get user agent
const getUserAgent = (): string => {
  return navigator.userAgent;
};

export class LoginLockedError extends Error {
  constructor(
    message: string,
    readonly lockedUntil: string,
  ) {
    super(message);
  }
}

export class ApiValidationError extends Error {}

export const authService = {
  async login(credentials: LoginCredentials): Promise<ApiLoginResponse> {
    try {
      const ipAddress = await getClientIpAddress();
      const userAgent = getUserAgent();

      const loginCommand: LoginUserCommand = {
        email: credentials.email,
        password: credentials.password,
        ipAddress,
        userAgent,
      };

      const response = await apiClient.api.authLoginCreate(loginCommand);
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Login failed:', error instanceof Error ? error.message : error);

      if (error.response) {
        switch (error.response.status) {
          case 401: {
            // Anti-enumeration (web-client#192): the login endpoint returns a
            // byte-identical 401 with NO reason field for every rejection —
            // wrong password, non-existent account, PendingApproval, Rejected,
            // Deleted. We MUST treat every 401 generically and NEVER branch on,
            // pass through, or log any field of the response body. Always throw
            // the same generic message regardless of body shape.
            throw new Error('Invalid email or password');
          }
          case 429:
            throw new LoginLockedError(
              'Too many failed sign-in attempts.',
              error.response.data?.lockedUntil,
            );
          case 400: {
            const validationErrors = error.response.data?.errors || {};
            const errorMessages = Object.values(validationErrors).flat();
            throw new Error(
              errorMessages.length > 0 ? errorMessages.join(', ') : 'Invalid request data',
            );
          }
          case 500:
            throw new Error('Server error. Please try again later.');
          default:
            throw new Error('Login failed. Please try again.');
        }
      }

      if (error.request) {
        throw new Error('Network error. Please check your connection.');
      }

      throw new Error('An unexpected error occurred');
    }
  },

  async verifyMfa(verificationData: MfaVerificationData): Promise<ApiLoginResponse> {
    try {
      const ipAddress = await getClientIpAddress();
      const userAgent = getUserAgent();

      const mfaCommand: CompleteMfaLoginCommand = {
        challengeId: verificationData.challengeId,
        code: verificationData.code,
        ipAddress,
        userAgent,
      };

      const response = await apiClient.api.authLoginVerifyMfaCreate(mfaCommand);
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('MFA verification error:', error instanceof Error ? error.message : error);

      if (
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'success' in error.response.data
      ) {
        return error.response.data as ApiLoginResponse;
      }

      if (error.response) {
        switch (error.response.status) {
          case 401:
            throw new Error('Invalid verification code');
          case 400:
            throw new Error('Invalid request. Please try again.');
          case 429:
            throw new Error('Too many attempts. Please try again later.');
          case 500:
            throw new Error('Server error. Please try again later.');
          default:
            throw new Error('Verification failed. Please try again.');
        }
      }

      if (error.request) {
        throw new Error('Network error. Please check your connection.');
      }

      throw new Error('An unexpected error occurred');
    }
  },

  async logout(): Promise<void> {
    // Anonymous endpoint: the server reads the refresh token from the httpOnly
    // cookie and revokes it, then clears the cookie. Best-effort — always returns
    // 204. Local state is cleared by the caller regardless of network outcome.
    try {
      await apiClient.api.authLogoutCreate({});
    } catch (error) {
      // Network failure only — don't rethrow.
      console.error('Logout failed:', error instanceof Error ? error.message : error);
    }
  },

  // ── Verify-first registration endpoints ─────────────────────────────────
  // Wave 2 (#188) — hand-authored against the verify-first contract.
  // Anti-enumeration: POST /api/auth/send-verification always returns a
  // success-shape regardless of whether the email exists. The client must
  // never fork on email existence.

  /**
   * Send a verification code to the given email.
   * Anti-enumeration: the server always returns a success-shape.
   * On network/server error, throws — caller surfaces a generic message.
   */
  async sendVerificationEmail(email: string): Promise<SendVerificationResponse> {
    try {
      const ipAddress = await getClientIpAddress();
      const userAgent = getUserAgent();
      const response = await apiClient.instance.post<SendVerificationResponse>(
        '/api/auth/send-verification',
        { email, ipAddress, userAgent },
      );
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error(
        'sendVerificationEmail failed:',
        error instanceof Error ? error.message : error,
      );
      if (error.request) {
        throw new Error('Network error. Please check your connection.');
      }
      throw new Error('Failed to send verification code. Please try again.');
    }
  },

  /**
   * Verify a 6-digit code against a challenge.
   * Returns success + optional typed reason on failure.
   * Verify-first: does NOT return a token on success.
   */
  async verifyEmail(challengeId: string, code: string): Promise<VerifyEmailResponse> {
    try {
      const ipAddress = await getClientIpAddress();
      const userAgent = getUserAgent();
      const response = await apiClient.instance.post<VerifyEmailResponse>(
        '/api/auth/verify-email',
        { challengeId, code, ipAddress, userAgent },
      );
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('verifyEmail failed:', error instanceof Error ? error.message : error);
      // Structured failure responses may arrive as 4xx — return them so the
      // UI can surface the typed reason.
      if (
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'success' in error.response.data
      ) {
        return error.response.data as VerifyEmailResponse;
      }
      if (error.request) {
        throw new Error('Network error. Please check your connection.');
      }
      throw new Error('Verification failed. Please try again.');
    }
  },

  /**
   * Resend a verification code to the email associated with the current challenge.
   * Server enforces cooldown / hourly cap / lockout — surfaces `resendAllowedAt` on refusal.
   */
  async resendVerification(email: string): Promise<ResendVerificationResponse> {
    try {
      const ipAddress = await getClientIpAddress();
      const userAgent = getUserAgent();
      const response = await apiClient.instance.post<ResendVerificationResponse>(
        '/api/auth/resend-verification',
        { email, ipAddress, userAgent },
      );
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('resendVerification failed:', error instanceof Error ? error.message : error);
      if (
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'success' in error.response.data
      ) {
        return error.response.data as ResendVerificationResponse;
      }
      if (error.request) {
        throw new Error('Network error. Please check your connection.');
      }
      throw new Error('Failed to resend code. Please try again.');
    }
  },

  /**
   * Redeem a password-reset token and set a new password.
   * Hand-wrapped against POST /api/auth/reset-password (mirrors
   * sendVerificationEmail) until the generated client method exists.
   * On a structured 400 the typed reason is returned so the UI can branch on
   * the PasswordResetFailureReason member string.
   */
  async resetPassword(request: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    try {
      const response = await apiClient.instance.post<ResetPasswordResponse>(
        '/api/auth/reset-password',
        request,
      );
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('resetPassword failed:', error instanceof Error ? error.message : error);
      // Structured failure responses arrive as 4xx — return them so the UI can
      // surface the typed reason.
      if (
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'success' in error.response.data
      ) {
        return error.response.data as ResetPasswordResponse;
      }
      // A refused new password arrives as a validation problem, before the token is checked.
      const validationErrors = Object.values(error.response?.data?.errors ?? {}).flat();
      if (error.response?.status === 400 && validationErrors.length > 0) {
        throw new ApiValidationError(validationErrors.join(', '));
      }
      if (error.request) {
        throw new Error('Network error. Please check your connection.');
      }
      throw new Error('Failed to reset password. Please try again.');
    }
  },

  /**
   * Request a password-reset link for the given email.
   * Hand-wrapped against POST /api/auth/forgot-password (mirrors
   * sendVerificationEmail / resetPassword) until the generated client method
   * exists.
   * Anti-enumeration: the server always returns a neutral 200 regardless of
   * whether the account exists — the caller must never branch on existence and
   * shows the same neutral confirmation on success AND on any error.
   */
  async forgotPassword(request: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
    try {
      const response = await apiClient.instance.post<ForgotPasswordResponse>(
        '/api/auth/forgot-password',
        request,
      );
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('forgotPassword failed:', error instanceof Error ? error.message : error);
      // The page surfaces the SAME neutral confirmation on error, so a thrown
      // failure is non-fatal to the user experience — rethrow with a generic
      // message; the caller logs to console.debug and shows the neutral copy.
      if (error.request) {
        throw new Error('Network error. Please check your connection.');
      }
      throw new Error('Failed to request a reset link. Please try again.');
    }
  },

  register: async (registerCredentials: RegisterAccountCredentials) => {
    try {
      const apiCommand = authMapper.toApi(registerCredentials);
      const response = await apiClient.api.authRegisterCreate(apiCommand);
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Registration failed:', error instanceof Error ? error.message : error);

      if (error.response) {
        switch (error.response.status) {
          case 409:
            throw new Error('An account with this email already exists');
          case 400: {
            const validationErrors = error.response.data?.errors || {};
            const errorMessages = Object.values(validationErrors).flat();
            throw new ApiValidationError(
              errorMessages.length > 0 ? errorMessages.join(', ') : 'Invalid registration data',
            );
          }
          case 500:
            throw new Error('Server error. Please try again later.');
          default:
            throw new Error('Registration failed. Please try again.');
        }
      }

      if (error.request) {
        throw new Error('Network error. Please check your connection.');
      }

      throw new Error('An unexpected error occurred');
    }
  },
};
